import { useState, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, Navigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, Input, Segmented, Select, StatusDot, TextArea, useToast, type IconName, type Tone } from "@agentsaas/ui";
import { pickLocalized, type Listing } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatBytes, formatDateTime, formatNumber, formatRelative } from "../app/format";
import { Loading } from "../layout/Loading";
import { ShellSlot } from "../layout/shell";
import { ak, useAudit, useIsPlatformAdmin, useNodes, usePlacements, useReviewQueue, type AuditFilter, type NodeInfo, type Placement } from "../data/admin";
import { LISTING_TONE, priceParams } from "../market/model";

/**
 * /admin/**：平台管理控制台（节点、放置、审计、市场审核）。
 * 只有平台管理员可见：进入时用 adminNodes 探测，403 显示无权限。
 * 迁移 / 钉住 / 审核（驳回、下架）都必须填写原因，原因进入审计日志。
 */

type AdminSection = "nodes" | "placements" | "audit" | "market";
const SECTIONS: { id: AdminSection; icon: IconName }[] = [
  { id: "nodes", icon: "server" },
  { id: "placements", icon: "move" },
  { id: "audit", icon: "list" },
  { id: "market", icon: "store" },
];

export const NODE_TONE: Record<NodeInfo["status"], Tone> = { online: "success", draining: "warning", unreachable: "danger", revoked: "neutral" };

function AdminFrame({ section, children }: { section: AdminSection; children: ReactNode }) {
  const { t } = useT();
  const admin = useIsPlatformAdmin();
  return (
    <>
      <ShellSlot name="title">
        <Icon name="shield" size={16} />
        <strong className="ellipsis">{t("admin.title")}</strong>
      </ShellSlot>
      {admin === undefined ? (
        <Loading />
      ) : !admin ? (
        <div className="page">
          <EmptyState icon="lock" title={t("admin.forbidden")}>
            <Link to="/" className="ui-btn ui-btn--secondary ui-btn--md">
              {t("nav.projects")}
            </Link>
          </EmptyState>
        </div>
      ) : (
        <div className="subframe">
          <nav className="subnav" aria-label={t("admin.title")}>
            {SECTIONS.map((s) => (
              <NavLink
                key={s.id}
                to={`/admin/${s.id}`}
                className={`pnav__link${section === s.id ? " is-active" : ""}`}
                aria-current={section === s.id ? "page" : undefined}
                data-testid={`admin-nav-${s.id}`}
              >
                <Icon name={s.icon} size={16} />
                {t(`admin.nav.${s.id}`)}
              </NavLink>
            ))}
          </nav>
          <div className="subframe__body">
            <div className="page page--wide">{children}</div>
          </div>
        </div>
      )}
    </>
  );
}

export function AdminIndex() {
  return <Navigate to="/admin/nodes" replace />;
}

/** 管理员入口链接（非管理员不渲染） */
export function AdminLink({ className }: { className?: string }) {
  const { t } = useT();
  const admin = useIsPlatformAdmin();
  if (!admin) return null;
  return (
    <Link to="/admin/nodes" className={className ?? "ui-btn ui-btn--secondary ui-btn--md"} data-testid="admin-link">
      <Icon name="shield" size={16} />
      {t("admin.title")}
    </Link>
  );
}

// ───────────── 原因对话框（迁移 / 钉住 / 审核共用） ─────────────

function ReasonDialog({
  open,
  title,
  confirmLabel,
  danger,
  optional,
  busy,
  error,
  valid = true,
  onClose,
  onSubmit,
  children,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  danger?: boolean;
  optional?: boolean;
  busy: boolean;
  error: string | null;
  valid?: boolean;
  onClose(): void;
  onSubmit(reason: string): void;
  children?: ReactNode;
}) {
  const { t } = useT();
  const [reason, setReason] = useState("");
  const ok = valid && (optional || reason.trim().length > 0);
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (ok) onSubmit(reason.trim());
  };
  const close = () => {
    setReason("");
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={title}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button variant={danger ? "danger" : "primary"} loading={busy} disabled={!ok} onClick={() => submit()} data-testid="reason-confirm">
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        {children}
        <Field label={optional ? t("admin.reason_optional") : t("admin.reason")} hint={t("admin.reason_hint")}>
          {(id, d) => <TextArea id={id} aria-describedby={d} aria-required={!optional} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reason-input" />}
        </Field>
      </form>
    </Dialog>
  );
}

// ───────────── 节点 ─────────────

function pct(n: number) {
  return Math.round(Math.min(1, Math.max(0, n)) * 100);
}

function Meter({ label, value, text }: { label: string; value: number; text: string }) {
  const tone = value >= 90 ? "danger" : value >= 75 ? "warning" : "ok";
  return (
    <div className="meter">
      <div className="meter__label small">
        <span className="muted">{label}</span>
        <span className="num">{text}</span>
      </div>
      <div className="budget__track" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value} aria-valuetext={text}>
        <div className={`budget__fill budget__fill--${tone}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export function NodesPage() {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const nodes = useNodes();
  const [tokenOpen, setTokenOpen] = useState(false);
  const [revoking, setRevoking] = useState<NodeInfo | null>(null);
  const [busy, setBusy] = useState(false);

  const revoke = async () => {
    if (!revoking) return;
    setBusy(true);
    try {
      await api.call("adminNodeRevoke", { params: { nid: revoking.id } });
      toast.show({ message: t("admin.node.revoked", { name: revoking.hostname }), tone: "success" });
      setRevoking(null);
      void qc.invalidateQueries({ queryKey: ak.nodes });
      void qc.invalidateQueries({ queryKey: ak.placementsAll });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminFrame section="nodes">
      <div className="page__head">
        <h1 className="page__title">{t("admin.nav.nodes")}</h1>
        <Button variant="primary" icon="key" onClick={() => setTokenOpen(true)} data-testid="join-token">
          {t("admin.node.join_token")}
        </Button>
      </div>
      {nodes.isPending ? (
        <Loading />
      ) : nodes.data?.length ? (
        <ul className="node-grid" data-testid="node-list">
          {nodes.data.map((n) => (
            <li key={n.id} className="card node-card" data-testid="node-card" data-status={n.status}>
              <div className="row">
                <StatusDot tone={NODE_TONE[n.status]} label={t(`admin.node.status.${n.status}`)} pulse={n.status === "online"} />
                <strong className="grow ellipsis mono">{n.hostname}</strong>
                <Badge tone={NODE_TONE[n.status]}>{t(`admin.node.status.${n.status}`)}</Badge>
              </div>
              {n.load ? (
                <div className="stack node-card__load">
                  <Meter label={t("admin.node.cpu")} value={pct(n.load.cpu)} text={formatNumber(n.load.cpu, locale, { style: "percent", maximumFractionDigits: 0 })} />
                  <Meter
                    label={t("admin.node.memory")}
                    value={pct(n.load.memUsedMb / n.load.memTotalMb)}
                    text={t("admin.node.of", { used: formatBytes(n.load.memUsedMb * 1024 * 1024, locale), total: formatBytes(n.load.memTotalMb * 1024 * 1024, locale) })}
                  />
                </div>
              ) : (
                <p className="small muted">{t("admin.node.no_load")}</p>
              )}
              <dl className="kv small">
                <dt>{t("admin.node.agents")}</dt>
                <dd className="num" data-testid="node-agents">
                  {formatNumber(n.agents, locale)}
                </dd>
                {n.load ? (
                  <>
                    <dt>{t("admin.node.disk_free")}</dt>
                    <dd className="num">{formatBytes(n.load.diskFreeMb * 1024 * 1024, locale)}</dd>
                  </>
                ) : null}
                <dt>{t("admin.node.last_seen")}</dt>
                <dd>{n.lastSeenAt ? <time dateTime={n.lastSeenAt}>{formatRelative(n.lastSeenAt, locale)}</time> : t("admin.never")}</dd>
                <dt>{t("admin.node.runtimes")}</dt>
                <dd>{n.runtimes.join(", ")}</dd>
              </dl>
              <div className="row node-card__actions">
                <Link to={`/admin/placements?node=${n.id}`} className="ui-btn ui-btn--ghost ui-btn--sm">
                  {t("admin.node.view_agents")}
                </Link>
                <span className="grow" />
                {n.status !== "revoked" ? (
                  <Button size="sm" variant="danger" onClick={() => setRevoking(n)} data-testid="node-revoke">
                    {t("admin.node.revoke")}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="server" title={t("admin.node.empty")} />
      )}
      <JoinTokenDialog open={tokenOpen} onClose={() => setTokenOpen(false)} />
      <Dialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title={t("admin.node.revoke_title", { name: revoking?.hostname ?? "" })}
        closeLabel={t("shell.close")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={busy} onClick={() => void revoke()} data-testid="node-revoke-confirm">
              {t("admin.node.revoke")}
            </Button>
          </>
        }
      >
        <p>{t("admin.node.revoke_body", { count: revoking?.agents ?? 0 })}</p>
      </Dialog>
    </AdminFrame>
  );
}

const TTLS = [600, 3600, 86400] as const;

function JoinTokenDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const tz = useSession().state.user?.timezone;
  const [ttl, setTtl] = useState<number>(3600);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ token: string; expiresAt: string; gateway: string } | null>(null);
  const close = () => {
    setIssued(null);
    setError(null);
    onClose();
    // 新节点加入后刷新列表
    void qc.invalidateQueries({ queryKey: ak.nodes });
  };
  const issue = async () => {
    setBusy(true);
    setError(null);
    try {
      setIssued(await api.call("adminJoinToken", { body: { ttlSec: ttl } }));
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const command = issued ? `agentsaas-node join --gateway ${issued.gateway} --token ${issued.token}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      toast.show({ message: t("admin.copied"), tone: "success", duration: 1500 });
    } catch {
      toast.show({ message: t("meeting.copy_failed"), tone: "danger" });
    }
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={t("admin.node.join_token")}
      closeLabel={t("shell.close")}
      footer={
        issued ? (
          <Button variant="primary" onClick={close}>
            {t("admin.done")}
          </Button>
        ) : (
          <>
            <Button onClick={close}>{t("common.cancel")}</Button>
            <Button variant="primary" loading={busy} onClick={() => void issue()} data-testid="join-token-issue">
              {t("admin.node.issue")}
            </Button>
          </>
        )
      }
    >
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {issued ? (
        <div className="stack" data-testid="join-token-result">
          <p className="small">{t("admin.node.token_hint")}</p>
          <pre className="codeblock mono small" data-testid="join-token-command">
            {command}
          </pre>
          <div className="row">
            <Button size="sm" icon="copy" onClick={() => void copy()}>
              {t("meeting.copy")}
            </Button>
            <span className="small muted">{t("admin.node.expires", { time: formatDateTime(issued.expiresAt, locale, tz) })}</span>
          </div>
        </div>
      ) : (
        <Field label={t("admin.node.ttl")} hint={t("admin.node.ttl_hint")}>
          {(id, d) => (
            <Select id={id} aria-describedby={d} value={ttl} onChange={(e) => setTtl(Number(e.target.value))} data-testid="join-token-ttl">
              {TTLS.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.node.ttl.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </Dialog>
  );
}

// ───────────── 放置 ─────────────

export function PlacementsPage() {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const [sp, setSp] = useSearchParams();
  const nodeFilter = sp.get("node") ?? "";
  const [agentFilter, setAgentFilter] = useState("");
  const nodes = useNodes();
  const placements = usePlacements(nodeFilter || undefined);
  const [dialog, setDialog] = useState<{ kind: "migrate" | "pin"; p: Placement } | null>(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nodeList = nodes.data ?? [];
  const nameOf = (nid: string | null) => (nid ? (nodeList.find((n) => n.id === nid)?.hostname ?? nid) : t("admin.placement.unplaced"));
  const online = nodeList.filter((n) => n.status === "online");
  const items = (placements.data ?? []).filter((p) => !agentFilter.trim() || p.agentId.includes(agentFilter.trim()));

  const onOpen = (kind: "migrate" | "pin", p: Placement) => {
    setError(null);
    const choices = online.filter((n) => kind === "pin" || n.id !== p.nodeId);
    setTarget(kind === "pin" ? (p.pinned ? "" : (p.nodeId ?? choices[0]?.id ?? "")) : (choices[0]?.id ?? ""));
    setDialog({ kind, p });
  };

  const submit = async (reason: string) => {
    if (!dialog) return;
    setBusy(true);
    setError(null);
    try {
      const aid = dialog.p.agentId;
      if (dialog.kind === "migrate") {
        await api.call("adminMigrate", { params: { aid }, body: { targetNodeId: target as never, reason } });
        toast.show({ message: t("admin.placement.migrated", { node: nameOf(target) }), tone: "success" });
      } else {
        await api.call("adminPin", { params: { aid }, body: { nodeId: (target || null) as never, reason } });
        toast.show({ message: target ? t("admin.placement.pinned_to", { node: nameOf(target) }) : t("admin.placement.unpinned"), tone: "success" });
      }
      setDialog(null);
      void qc.invalidateQueries({ queryKey: ak.placementsAll });
      void qc.invalidateQueries({ queryKey: ak.nodes });
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminFrame section="placements">
      <div className="page__head">
        <h1 className="page__title">{t("admin.nav.placements")}</h1>
      </div>
      <div className="filters row">
        <Select aria-label={t("admin.placement.filter_node")} value={nodeFilter} onChange={(e) => setSp(e.target.value ? { node: e.target.value } : {}, { replace: true })} data-testid="placement-node-filter">
          <option value="">{t("admin.placement.all_nodes")}</option>
          {nodeList.map((n) => (
            <option key={n.id} value={n.id}>
              {n.hostname}
            </option>
          ))}
        </Select>
        <Input type="search" aria-label={t("admin.placement.filter_agent")} placeholder={t("admin.placement.filter_agent")} value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} />
      </div>
      {placements.isPending ? (
        <Loading />
      ) : items.length ? (
        <div className="table-wrap card">
          <table className="table table--stack" data-testid="placement-table">
            <thead>
              <tr>
                <th scope="col">{t("admin.placement.agent")}</th>
                <th scope="col">{t("admin.placement.node")}</th>
                <th scope="col">{t("admin.placement.pinned")}</th>
                <th scope="col">
                  <span className="ui-sr-only">{t("admin.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.agentId} data-testid="placement-row" data-agent={p.agentId}>
                  <td className="mono small">{p.agentId}</td>
                  <td data-testid="placement-node">{nameOf(p.nodeId)}</td>
                  <td>{p.pinned ? <Badge tone="accent">{t("admin.placement.pinned")}</Badge> : <span className="muted small">{t("admin.placement.auto")}</span>}</td>
                  <td className="table__actions">
                    <Button size="sm" icon="move" onClick={() => onOpen("migrate", p)} disabled={online.filter((n) => n.id !== p.nodeId).length === 0} data-testid="placement-migrate">
                      {t("admin.placement.migrate")}
                    </Button>
                    <Button size="sm" icon="pin" variant="ghost" onClick={() => onOpen("pin", p)} data-testid="placement-pin">
                      {p.pinned ? t("admin.placement.edit_pin") : t("admin.placement.pin")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon="move" title={t("admin.placement.empty")} />
      )}
      <ReasonDialog
        key={dialog ? `${dialog.kind}:${dialog.p.agentId}` : "none"}
        open={!!dialog}
        title={dialog?.kind === "migrate" ? t("admin.placement.migrate_title") : t("admin.placement.pin_title")}
        confirmLabel={dialog?.kind === "migrate" ? t("admin.placement.migrate") : target ? t("admin.placement.pin") : t("admin.placement.unpin")}
        busy={busy}
        error={error}
        valid={dialog?.kind === "pin" || !!target}
        onClose={() => setDialog(null)}
        onSubmit={(r) => void submit(r)}
      >
        {dialog ? (
          <>
            <dl className="kv small">
              <dt>{t("admin.placement.agent")}</dt>
              <dd className="mono">{dialog.p.agentId}</dd>
              <dt>{t("admin.placement.current")}</dt>
              <dd>{nameOf(dialog.p.nodeId)}</dd>
            </dl>
            <Field label={dialog.kind === "migrate" ? t("admin.placement.target") : t("admin.placement.pin_node")}>
              {(id) => (
                <Select id={id} value={target} onChange={(e) => setTarget(e.target.value)} data-testid="placement-target">
                  {dialog.kind === "pin" ? <option value="">{dialog.p.pinned ? t("admin.placement.unpin") : t("admin.placement.choose")}</option> : null}
                  {online
                    .filter((n) => dialog.kind === "pin" || n.id !== dialog.p.nodeId)
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {t("admin.placement.node_option", { name: n.hostname, count: n.agents })}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          </>
        ) : null}
      </ReasonDialog>
    </AdminFrame>
  );
}

// ───────────── 审计 ─────────────

export function AuditPage() {
  const { t, locale } = useT();
  const tz = useSession().state.user?.timezone;
  const [draft, setDraft] = useState<AuditFilter>({});
  const [filter, setFilter] = useState<AuditFilter>({});
  const audit = useAudit(filter);
  const clean = (f: AuditFilter): AuditFilter => Object.fromEntries(Object.entries(f).filter(([, v]) => typeof v === "string" && v.trim()).map(([k, v]) => [k, (v as string).trim()]));
  return (
    <AdminFrame section="audit">
      <div className="page__head">
        <h1 className="page__title">{t("admin.nav.audit")}</h1>
      </div>
      <form
        className="filters row"
        onSubmit={(e) => {
          e.preventDefault();
          setFilter(clean(draft));
        }}
        role="search"
      >
        <Input aria-label={t("admin.audit.actor")} placeholder={t("admin.audit.actor")} value={draft.actor ?? ""} onChange={(e) => setDraft({ ...draft, actor: e.target.value })} data-testid="audit-actor" />
        <Input aria-label={t("admin.audit.action")} placeholder={t("admin.audit.action_hint")} value={draft.action ?? ""} onChange={(e) => setDraft({ ...draft, action: e.target.value })} data-testid="audit-action" />
        <Input aria-label={t("admin.audit.tenant")} placeholder={t("admin.audit.tenant")} value={draft.tenantId ?? ""} onChange={(e) => setDraft({ ...draft, tenantId: e.target.value as never })} />
        <Button type="submit" icon="refresh" data-testid="audit-apply">
          {t("admin.audit.apply")}
        </Button>
      </form>
      {audit.isPending ? (
        <Loading />
      ) : audit.isError ? (
        <EmptyState icon="list" title={t("error.common.validation_failed")} />
      ) : audit.items.length ? (
        <>
          <ul className="list card audit-list" style={{ padding: 0 }} data-testid="audit-list">
            {audit.items.map((e) => (
              <li key={e.id} className="audit-item" data-testid="audit-item" data-action={e.action}>
                <details>
                  <summary className="audit-item__sum">
                    <span className="audit-item__main">
                      <code className="audit-item__action">{e.action}</code>
                      <span className="small ellipsis mono">{e.target}</span>
                    </span>
                    <span className="small muted audit-item__meta">
                      {e.actor}
                      <span className="sep" aria-hidden="true" />
                      <time dateTime={e.at}>{formatDateTime(e.at, locale, tz)}</time>
                    </span>
                    {e.reason ? (
                      <span className="small audit-item__reason" data-testid="audit-reason">
                        {t("admin.audit.reason", { reason: e.reason })}
                      </span>
                    ) : null}
                  </summary>
                  <dl className="kv small audit-item__detail">
                    <dt>{t("admin.audit.tenant")}</dt>
                    <dd className="mono">{e.tenantId ?? t("admin.audit.platform")}</dd>
                    <dt>{t("admin.audit.data")}</dt>
                    <dd>
                      <pre className="codeblock mono">{JSON.stringify(e.data, null, 2)}</pre>
                    </dd>
                  </dl>
                </details>
              </li>
            ))}
          </ul>
          {audit.hasNextPage ? (
            <div className="row" style={{ justifyContent: "center", marginTop: "var(--space-3)" }}>
              <Button onClick={() => void audit.fetchNextPage()} loading={audit.isFetchingNextPage} data-testid="audit-more">
                {t("admin.load_more")}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState icon="list" title={t("admin.audit.empty")} />
      )}
    </AdminFrame>
  );
}

// ───────────── 市场审核 ─────────────

type ReviewFilter = "pending_review" | "published" | "suspended";
const REVIEW_FILTERS: ReviewFilter[] = ["pending_review", "published", "suspended"];

export function MarketReviewPage() {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const [sp] = useSearchParams();
  const focus = sp.get("lid");
  const [filter, setFilter] = useState<ReviewFilter>("pending_review");
  const queue = useReviewQueue();
  // 通知 / 深链接里的单个商品（可能不在搜索结果里）
  const focused = useQuery({ queryKey: ["listing", focus ?? ""], enabled: !!focus, queryFn: () => api.call("listingGet", { params: { lid: focus! } }) });
  const [dialog, setDialog] = useState<{ l: Listing; decision: "publish" | "reject" | "suspend" } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const all = [...(queue.data ?? [])];
  if (focused.data && !all.some((x) => x.id === focused.data.id)) all.unshift(focused.data);
  const items = all.filter((l) => l.status === filter);
  const count = (f: ReviewFilter) => all.filter((l) => l.status === f).length;

  const onAsk = (l: Listing, decision: "publish" | "reject" | "suspend") => {
    setError(null);
    setDialog({ l, decision });
  };

  const submit = async (reason: string) => {
    if (!dialog) return;
    setBusy(true);
    setError(null);
    try {
      await api.call("adminListingModerate", { params: { lid: dialog.l.id }, body: { decision: dialog.decision, reason } });
      toast.show({ message: t(`admin.review.done.${dialog.decision}`), tone: "success" });
      setDialog(null);
      void qc.invalidateQueries({ queryKey: ak.review });
      void qc.invalidateQueries({ queryKey: ["market"] });
      if (focus) void qc.invalidateQueries({ queryKey: ["listing", focus] });
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminFrame section="market">
      <div className="page__head">
        <h1 className="page__title">{t("admin.nav.market")}</h1>
        <Segmented<ReviewFilter>
          label={t("admin.review.filter")}
          value={filter}
          onChange={setFilter}
          options={REVIEW_FILTERS.map((f) => ({ value: f, label: `${t(`market.status.${f}`)} ${count(f)}` }))}
        />
      </div>
      {queue.isPending ? (
        <Loading />
      ) : items.length ? (
        <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0 }} data-testid="review-list">
          {items.map((l) => {
            const price = priceParams(l.pricing, locale);
            return (
              <li key={l.id} className={`card review-card${l.id === focus ? " is-focus" : ""}`} data-testid="review-item" data-status={l.status}>
                <div className="row">
                  <strong className="grow">{pickLocalized(l.title, locale)}</strong>
                  <Badge tone={LISTING_TONE[l.status]}>{t(`market.status.${l.status}`)}</Badge>
                </div>
                <p className="small">{pickLocalized(l.description, locale)}</p>
                <dl className="kv small">
                  <dt>{t("market.role")}</dt>
                  <dd>{l.role}</dd>
                  <dt>{t("market.pricing")}</dt>
                  <dd>{t(price.key, { price: price.price })}</dd>
                  <dt>{t("admin.review.tenant")}</dt>
                  <dd className="mono">{l.tenantId}</dd>
                  <dt>{t("admin.review.submitted")}</dt>
                  <dd>{formatRelative(l.createdAt, locale)}</dd>
                  <dt>{t("admin.review.languages")}</dt>
                  <dd>{Object.keys(l.title).join(", ")}</dd>
                </dl>
                <div className="row">
                  {l.status !== "published" ? (
                    <Button variant="primary" size="sm" icon="check" onClick={() => onAsk(l, "publish")} data-testid="review-publish">
                      {t("admin.review.publish")}
                    </Button>
                  ) : null}
                  {l.status === "pending_review" ? (
                    <Button size="sm" onClick={() => onAsk(l, "reject")} data-testid="review-reject">
                      {t("admin.review.reject")}
                    </Button>
                  ) : null}
                  {l.status === "published" ? (
                    <Button size="sm" variant="danger" onClick={() => onAsk(l, "suspend")} data-testid="review-suspend">
                      {t("admin.review.suspend")}
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="store" title={t("admin.review.empty")} />
      )}
      <ReasonDialog
        key={dialog ? `${dialog.l.id}:${dialog.decision}` : "none"}
        open={!!dialog}
        title={dialog ? t(`admin.review.title.${dialog.decision}`, { title: pickLocalized(dialog.l.title, locale) }) : ""}
        confirmLabel={dialog ? t(`admin.review.${dialog.decision}`) : ""}
        danger={dialog?.decision !== "publish"}
        optional={dialog?.decision === "publish"}
        busy={busy}
        error={error}
        onClose={() => setDialog(null)}
        onSubmit={(r) => void submit(r)}
      />
    </AdminFrame>
  );
}
